import type {
  ClassReportResponse,
  DistrictReportResponse,
  PortalClass,
  PortalDistrict,
  PortalHomeResponse,
  PortalNavigationResponse,
  PortalPopulationReferenceResponse,
  PortalSchool,
  PortalSessionResponse,
  PortalStudent,
  SchoolReportResponse,
  StudentReportResponse,
} from "@/contracts/portal";
import type {
  PortalAssessmentForTerm,
  PortalCompletionStatus,
  PortalResultAvailability,
  PortalRiskBand,
} from "@/contracts/domain";

export class PortalContractError extends Error {
  path: string;

  constructor(path: string, message: string) {
    super(message);
    this.name = "PortalContractError";
    this.path = path;
  }
}

const RISK_BANDS = new Set<PortalRiskBand>(["low", "moderate", "high", "more_info"]);
const STATUSES = new Set<PortalCompletionStatus>(["completed", "partial", "notstarted"]);
const AVAILABILITIES = new Set<PortalResultAvailability>(["available", "withheld", "no_results"]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireObject(value: unknown, path: string): Record<string, unknown> {
  if (!isObject(value)) throw new PortalContractError(path, `Expected object at ${path}`);
  return value;
}

function requireArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new PortalContractError(path, `Expected array at ${path}`);
  return value;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new PortalContractError(path, `Expected non-empty string at ${path}`);
  }
  return value;
}

function coerceRisk(value: unknown): PortalRiskBand {
  return RISK_BANDS.has(value as PortalRiskBand) ? value as PortalRiskBand : "more_info";
}

function coerceStatus(value: unknown): PortalCompletionStatus {
  return STATUSES.has(value as PortalCompletionStatus) ? value as PortalCompletionStatus : "notstarted";
}

function coerceHeadlineBand(value: unknown): PortalRiskBand | null {
  if (value == null) return null;
  return coerceRisk(value);
}

function coerceAvailability(container: Record<string, unknown>): void {
  if (!("availability" in container)) return;
  if (!AVAILABILITIES.has(container.availability as PortalResultAvailability)) {
    /* Fail closed: a present-but-unrecognised availability must never fall back
       to the legacy "field absent" path, which could display the result. */
    container.availability = "withheld";
  }
}

function coerceCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

function validateAssessment(raw: unknown, path: string): PortalAssessmentForTerm {
  const assessment = requireObject(raw, path);
  /* Always coerce, so absent/invalid values fail closed instead of poisoning
     dynamic-key counters downstream (counts[risk]++ with an unknown key). */
  assessment.risk = coerceRisk(assessment.risk);
  assessment.status = coerceStatus(assessment.status);
  assessment.tasksDone = coerceCount(assessment.tasksDone);
  assessment.tasksTotal = coerceCount(assessment.tasksTotal);
  if ("headlineBand" in assessment) assessment.headlineBand = coerceHeadlineBand(assessment.headlineBand);
  coerceAvailability(assessment);
  return assessment as unknown as PortalAssessmentForTerm;
}

function validateStudent(raw: unknown, path: string): PortalStudent {
  const student = requireObject(raw, path);
  requireString(student.id, `${path}.id`);
  requireString(student.name, `${path}.name`);
  const assessments = requireObject(student.assessments, `${path}.assessments`);
  Object.entries(assessments).forEach(([term, assessment]) => {
    assessments[term] = validateAssessment(assessment, `${path}.assessments.${term}`);
  });

  const summary = requireObject(student.summary, `${path}.summary`);
  summary.risk = coerceRisk(summary.risk);
  summary.status = coerceStatus(summary.status);
  summary.tasksDone = coerceCount(summary.tasksDone);
  summary.tasksTotal = coerceCount(summary.tasksTotal);
  if ("headlineBand" in summary) summary.headlineBand = coerceHeadlineBand(summary.headlineBand);
  coerceAvailability(summary);

  requireObject(student.context, `${path}.context`);
  requireObject(student.riskTrajectory, `${path}.riskTrajectory`);
  return student as unknown as PortalStudent;
}

function validateClass(raw: unknown, path: string): PortalClass {
  const klass = requireObject(raw, path);
  requireString(klass.id, `${path}.id`);
  requireString(klass.name, `${path}.name`);
  const students = requireArray(klass.students, `${path}.students`);
  students.forEach((student, index) => {
    students[index] = validateStudent(student, `${path}.students[${index}]`);
  });
  return klass as unknown as PortalClass;
}

function validateSchool(raw: unknown, path: string): PortalSchool {
  const school = requireObject(raw, path);
  requireString(school.id, `${path}.id`);
  requireString(school.name, `${path}.name`);
  const classes = requireArray(school.classes, `${path}.classes`);
  classes.forEach((klass, index) => {
    classes[index] = validateClass(klass, `${path}.classes[${index}]`);
  });
  return school as unknown as PortalSchool;
}

function validateDistrict(raw: unknown, path: string): PortalDistrict {
  const district = requireObject(raw, path);
  requireString(district.id, `${path}.id`);
  requireString(district.name, `${path}.name`);
  const schools = requireArray(district.schools, `${path}.schools`);
  schools.forEach((school, index) => {
    schools[index] = validateSchool(school, `${path}.schools[${index}]`);
  });
  return district as unknown as PortalDistrict;
}

export function validateSessionResponse(raw: unknown): PortalSessionResponse {
  const session = requireObject(raw, "session");
  if ("user" in session && session.user != null) requireObject(session.user, "session.user");
  return session as unknown as PortalSessionResponse;
}

export function validateNavigationResponse(raw: unknown): PortalNavigationResponse {
  const navigation = requireObject(raw, "navigation");
  requireArray(navigation.scopes, "navigation.scopes");
  requireObject(navigation.defaultRoute, "navigation.defaultRoute");
  return navigation as unknown as PortalNavigationResponse;
}

export function validatePortalHomeResponse(raw: unknown): PortalHomeResponse {
  const home = requireObject(raw, "home");
  const districts = requireArray(home.districts, "districts");
  districts.forEach((district, index) => {
    districts[index] = validateDistrict(district, `districts[${index}]`);
  });
  return home as unknown as PortalHomeResponse;
}

export function validateDistrictReportResponse(raw: unknown): DistrictReportResponse | null {
  if (raw === null) return null;
  const report = requireObject(raw, "districtReport");
  report.district = validateDistrict(report.district, "district");
  return report as unknown as DistrictReportResponse;
}

export function validateSchoolReportResponse(raw: unknown): SchoolReportResponse | null {
  if (raw === null) return null;
  const report = requireObject(raw, "schoolReport");
  report.district = validateDistrict(report.district, "district");
  report.school = validateSchool(report.school, "school");
  return report as unknown as SchoolReportResponse;
}

export function validateClassReportResponse(raw: unknown): ClassReportResponse | null {
  if (raw === null) return null;
  const report = requireObject(raw, "classReport");
  report.district = validateDistrict(report.district, "district");
  report.school = validateSchool(report.school, "school");
  report.klass = validateClass(report.klass, "klass");
  return report as unknown as ClassReportResponse;
}

export function validateStudentReportResponse(raw: unknown): StudentReportResponse | null {
  if (raw === null) return null;
  const report = requireObject(raw, "studentReport");
  report.district = validateDistrict(report.district, "district");
  report.school = validateSchool(report.school, "school");
  report.klass = validateClass(report.klass, "klass");
  report.student = validateStudent(report.student, "student");
  return report as unknown as StudentReportResponse;
}

export function validatePopulationReferenceResponse(raw: unknown): PortalPopulationReferenceResponse | null {
  try {
    if (!isObject(raw)) return null;
    if (!isPopulationReferenceDistributions(raw.distributions)) return null;
    if (!isCompositeScoreDistributions(raw.compositeScoreDistributions)) return null;
    if (raw.distributions === undefined && raw.compositeScoreDistributions === undefined) return null;
    return raw as PortalPopulationReferenceResponse;
  } catch {
    return null;
  }
}

function isPopulationDistribution(value: unknown): boolean {
  if (!isObject(value)) return false;
  /* yearLevel is optional: the shipped static file keys distributions by year
     ("distributions": { "F": {...}, "Y1": {...} }) with no inner yearLevel. */
  if (value.yearLevel !== undefined && typeof value.yearLevel !== "string") return false;
  if (value.bandCutoffs !== undefined && !isObject(value.bandCutoffs)) return false;
  if (value.bins !== undefined && !Array.isArray(value.bins)) return false;
  if (value.compositeScores !== undefined && !Array.isArray(value.compositeScores)) return false;
  if (value.scores !== undefined && !Array.isArray(value.scores)) return false;
  /* Require at least one payload-bearing member so arbitrary objects don't pass. */
  return value.bandCutoffs !== undefined || value.bins !== undefined ||
    value.compositeScores !== undefined || value.scores !== undefined;
}

function isPopulationReferenceDistributions(value: unknown): boolean {
  if (value === undefined) return true;
  if (Array.isArray(value)) return value.every(isPopulationDistribution);
  if (!isObject(value)) return false;
  return Object.values(value).every((entry) => {
    if (entry === undefined) return true;
    if (isPopulationDistribution(entry)) return true;
    if (!isObject(entry)) return false;
    return Object.values(entry).every((nested) => nested === undefined || isPopulationDistribution(nested));
  });
}

function isCompositeScoreDistributions(value: unknown): boolean {
  if (value === undefined) return true;
  if (!isObject(value)) return false;
  return Object.values(value).every((scores) => scores === undefined || Array.isArray(scores));
}
